import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Logo } from '@/components/Logo';
import { supabase } from '@/integrations/supabase/client';

type State =
  | { kind: 'loading' }
  | { kind: 'valid' }
  | { kind: 'already' }
  | { kind: 'invalid' }
  | { kind: 'submitting' }
  | { kind: 'done' }
  | { kind: 'error'; message: string };

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    let active = true;
    (async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`;
        const res = await fetch(url, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        });
        const data = await res.json();
        if (!active) return;
        if (res.ok && data.valid) setState({ kind: 'valid' });
        else if (data?.reason === 'already_unsubscribed') setState({ kind: 'already' });
        else setState({ kind: 'invalid' });
      } catch {
        if (active) setState({ kind: 'invalid' });
      }
    })();
    return () => { active = false; };
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: 'submitting' });
    try {
      const { data, error } = await supabase.functions.invoke('handle-email-unsubscribe', {
        body: { token },
      });
      if (error) throw error;
      if ((data as any)?.success) setState({ kind: 'done' });
      else if ((data as any)?.reason === 'already_unsubscribed') setState({ kind: 'already' });
      else setState({ kind: 'error', message: 'Unable to process unsubscribe.' });
    } catch (e: any) {
      setState({ kind: 'error', message: e?.message ?? 'Something went wrong.' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="flex items-center justify-center gap-2">
          <Logo variant="square" className="h-10 w-10" />
          <span className="font-bold tracking-tight text-lg">PULL OVER SALES</span>
        </Link>

        <Card>
          <CardContent className="p-8 text-center space-y-4">
            {state.kind === 'loading' && (
              <>
                <Loader2 className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
                <p className="text-sm text-muted-foreground">Checking your link…</p>
              </>
            )}

            {state.kind === 'valid' && (
              <>
                <Mail className="h-10 w-10 mx-auto text-primary" />
                <h1 className="text-xl font-semibold">Unsubscribe from emails?</h1>
                <p className="text-sm text-muted-foreground">
                  You'll stop receiving daily digests and other updates from Pull Over Sales.
                </p>
                <Button onClick={confirm} className="w-full">Confirm unsubscribe</Button>
              </>
            )}

            {state.kind === 'submitting' && (
              <>
                <Loader2 className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
                <p className="text-sm text-muted-foreground">Processing…</p>
              </>
            )}

            {state.kind === 'done' && (
              <>
                <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />
                <h1 className="text-xl font-semibold">You're unsubscribed</h1>
                <p className="text-sm text-muted-foreground">
                  We won't send you any more emails. Changed your mind? Contact your dealership manager.
                </p>
              </>
            )}

            {state.kind === 'already' && (
              <>
                <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground" />
                <h1 className="text-xl font-semibold">Already unsubscribed</h1>
                <p className="text-sm text-muted-foreground">
                  This email address is already opted out of our messages.
                </p>
              </>
            )}

            {state.kind === 'invalid' && (
              <>
                <AlertCircle className="h-10 w-10 mx-auto text-destructive" />
                <h1 className="text-xl font-semibold">This link isn't valid</h1>
                <p className="text-sm text-muted-foreground">
                  The unsubscribe link may have expired. Please use the link in your most recent email.
                </p>
              </>
            )}

            {state.kind === 'error' && (
              <>
                <AlertCircle className="h-10 w-10 mx-auto text-destructive" />
                <h1 className="text-xl font-semibold">Something went wrong</h1>
                <p className="text-sm text-muted-foreground">{state.message}</p>
                <Button variant="outline" onClick={confirm} className="w-full">Try again</Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-center text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Pull Over Sales</Link>
        </p>
      </div>
    </div>
  );
}